import { isReadOnlySelector } from 'modules/MedComms/selectors';
import CitationList from 'modules/References/components/CitationList';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import AssetsCollectionNavBar from 'components/AssetsCollectionNavBar';
import ImageList from 'components/ImageList';
import LayoutList from 'components/LayoutList';
import TextComponentList from 'components/TextComponentList';
import { AssetsCollectionNavItem } from 'const';
import * as Models from 'models';
import { isModuleBundle } from 'utils/entityType';
import { intlGet } from 'utils/intlGet';
import { AssetsCollectionEntitiesProps } from './models';
import styles from './styles.module.scss';
import * as utils from './utils';

const AssetsCollectionEntities: React.FunctionComponent<AssetsCollectionEntitiesProps> = (props) => {
  const { document, screenOrder, screen, dataTestId = 'assetsCollectionEntities' } = props;
  const isReadOnly = useSelector(isReadOnlySelector);

  const entities = useMemo(() => utils.getAssetsCollectionEntities(document, screenOrder), [document, screenOrder]);
  const tabs = useMemo(() => utils.getNavItems(document), [document]);
  const disabledTabs = useMemo(() => utils.getDisabledTabs(entities), [entities]);
  const internalDocumentsExist = useMemo(() => utils.internalDocumentsExist(entities, document), [entities, document]);
  const [activeTab, setActiveTab] = useState(() => utils.getActiveTab(tabs, disabledTabs));

  const getContent = (): JSX.Element | null => {
    const { documents } = entities[activeTab];

    switch (activeTab) {
      case AssetsCollectionNavItem.REFERENCE_CITATIONS:
        return (<CitationList
          citations={documents as Models.ReferenceCitationsMap}
          storyCardId={(document as unknown as Models.DocumentMap).get('id')}
          dataTestId={`${dataTestId}-content-citationList`} />);
      case AssetsCollectionNavItem.TEXTS:
        return (<TextComponentList components={documents as Models.TextComponentsMap} showHeader={false}
          dataTestId={`${dataTestId}-content-textComponentList`} />);
      case AssetsCollectionNavItem.IMAGES:
        return <ImageList documents={documents as Models.ImagesMap} showHeader={false} dataTestId={`${dataTestId}-content-imageList`} />;
      case AssetsCollectionNavItem.LAYOUTS:
        return (<LayoutList
          isReadOnly={isReadOnly}
          screen={screen}
          reusableLayouts={documents as Models.CombinedLayoutDocumentsMap}
          storyCardId={(document as unknown as Models.DocumentMap).get('id') ?? ''}
          dataTestId={`${dataTestId}-content-reusableLayoutList`}
        />);

      default: return null;
    }
  };

  return internalDocumentsExist
    ? (
      <div className={styles.AssetsCollectionEntities} data-testid={dataTestId}>
        {!isModuleBundle(document) &&
        <AssetsCollectionNavBar
          navItems={tabs}
          activeItem={activeTab}
          onChange={setActiveTab}
          disabledItems={disabledTabs}
          dataTestId={`${dataTestId}-navbar`}
        />}
        <div className={styles.content} data-testid={`${dataTestId}-content`}>
          {getContent()}
        </div>
      </div>
    )
    : <div className={styles.empty} data-testid={`${dataTestId}-empty`}>{intlGet('AssetsCollectionEntities', 'NoContent')}</div>;
};

export default AssetsCollectionEntities;
